<template>
  <div class="service-cascade-filter space-y-4">
    <!-- First Level: Service Category -->
    <div>
      <label class="block text-sm font-medium text-gray-700 mb-2">
        <i class="fas fa-stethoscope text-accent-600 mr-1"></i>
        Service Category
      </label>
      <select
        v-model="selectedCategory"
        @change="onCategoryChange"
        :disabled="isLoading"
        class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
      >
        <option value="all">
          {{ isLoading ? 'Loading...' : 'All Service Categories' }}
        </option>
        <option 
          v-for="(categoryData, categoryKey) in serviceStructure" 
          :key="categoryKey" 
          :value="categoryKey"
        >
          {{ categoryData.label }}
        </option>
      </select>
    </div>

    <!-- Second Level: Service Type -->
    <div v-if="selectedCategory !== 'all' && availableServiceTypes.length > 0">
      <label class="block text-sm font-medium text-gray-700 mb-2">
        <i class="fas fa-list text-secondary-600 mr-1"></i>
        Service Type
      </label>

      <select
        v-model="selectedServiceType"
        @change="onServiceTypeChange"
        :disabled="isLoading"
        class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
      >
        <option value="all">All {{ getCategoryLabel(selectedCategory) }} Types</option>
        <option 
          v-for="serviceType in availableServiceTypes" 
          :key="serviceType.key" 
          :value="serviceType.key"
        >
          {{ serviceType.label }}
        </option>
      </select>
    </div>

    <!-- Third Level: Specific Services -->
    <div v-if="selectedServiceType !== 'all' && availableSpecificServices.length > 0">
      <label class="block text-sm font-medium text-gray-700 mb-2">
        <i class="fas fa-atom text-primary-600 mr-1"></i>
        Specific Service
      </label>

      <select
        v-model="selectedSpecificService"
        @change="onSpecificServiceChange"
        :disabled="isLoading"
        class="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
      >
        <option value="all">All {{ getServiceTypeLabel(selectedServiceType) }} Services</option>
        <option 
          v-for="specificService in availableSpecificServices" 
          :key="specificService.key" 
          :value="specificService.key"
        >
          {{ specificService.label }}
        </option>
      </select>
    </div>

    <!-- Active Filters Display -->
    <div v-if="hasActiveFilters" class="bg-blue-50 rounded-lg p-3 border border-blue-200">
      <div class="flex items-center justify-between">
        <div class="flex items-center">
          <i class="fas fa-filter text-blue-600 mr-2"></i>
          <span class="text-sm font-medium text-blue-900">Active Filters</span>
        </div>
        <button
          @click="resetFilters"
          class="text-xs text-blue-600 hover:text-blue-800 underline"
        >
          Clear All
        </button>
      </div>
      
      <div class="mt-2 space-y-1">
        <div v-if="selectedCategory !== 'all'" class="flex items-center text-sm text-blue-700">
          <i class="fas fa-tag mr-1"></i>
          <span>Category: {{ getCategoryLabel(selectedCategory) }}</span>
          <button @click="clearCategory" class="ml-2 text-blue-500 hover:text-blue-700">
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <div v-if="selectedServiceType !== 'all'" class="flex items-center text-sm text-blue-700">
          <i class="fas fa-list mr-1"></i>
          <span>Type: {{ getServiceTypeLabel(selectedServiceType) }}</span>
          <button @click="clearServiceType" class="ml-2 text-blue-500 hover:text-blue-700">
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <div v-if="selectedSpecificService !== 'all'" class="flex items-center text-sm text-blue-700">
          <i class="fas fa-atom mr-1"></i>
          <span>Service: {{ getSpecificServiceLabel(selectedSpecificService) }}</span>
          <button @click="clearSpecificService" class="ml-2 text-blue-500 hover:text-blue-700">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, watch, computed, onMounted } from 'vue'

export default {
  name: 'ServiceCascadeFilter',
  props: {
    initialFilters: {
      type: Object,
      default: () => ({
        category: 'all',
        serviceType: 'all',
        specificService: 'all'
      })
    }
  },
  emits: ['filtersChanged'],
  setup(props, { emit }) {
    // Loading state
    const isLoading = ref(true)
    
    // Service structure data from API
    const serviceStructure = ref({})
    
    // Filter state
    const selectedCategory = ref(props.initialFilters.category || 'all')
    const selectedServiceType = ref(props.initialFilters.serviceType || 'all')
    const selectedSpecificService = ref(props.initialFilters.specificService || 'all')

    // Computed properties
    const availableServiceTypes = computed(() => {
      if (selectedCategory.value === 'all' || !serviceStructure.value[selectedCategory.value]) {
        return []
      }
      
      const categoryData = serviceStructure.value[selectedCategory.value]
      return Object.entries(categoryData.services || {}).map(([key, data]) => ({
        key,
        ...data
      }))
    })

    const availableSpecificServices = computed(() => {
      if (selectedServiceType.value === 'all' || !serviceStructure.value[selectedCategory.value]) {
        return []
      }
      
      const serviceTypeData = serviceStructure.value[selectedCategory.value].services[selectedServiceType.value]
      if (!serviceTypeData || !serviceTypeData.subtypes) {
        return []
      }
      
      return Object.entries(serviceTypeData.subtypes).map(([key, data]) => ({
        key,
        ...data
      }))
    })

    const hasActiveFilters = computed(() => {
      return selectedCategory.value !== 'all' || 
             selectedServiceType.value !== 'all' || 
             selectedSpecificService.value !== 'all'
    })

    const currentServiceQuery = computed(() => {
      // Return the most specific selection
      if (selectedSpecificService.value !== 'all') {
        return selectedSpecificService.value
      }
      if (selectedServiceType.value !== 'all') {
        return selectedServiceType.value
      }
      if (selectedCategory.value !== 'all') {
        return selectedCategory.value
      }
      return null
    })

    // Helper methods
    const getCategoryLabel = (category) => {
      return serviceStructure.value[category]?.label || category
    }

    const getServiceTypeLabel = (serviceType) => {
      if (selectedCategory.value === 'all') return serviceType
      const serviceTypeData = serviceStructure.value[selectedCategory.value]?.services?.[serviceType]
      return serviceTypeData?.label || serviceType
    }

    const getSpecificServiceLabel = (specificService) => {
      if (selectedServiceType.value === 'all') return specificService
      const specificServiceData = serviceStructure.value[selectedCategory.value]?.services?.[selectedServiceType.value]?.subtypes?.[specificService]
      return specificServiceData?.label || specificService
    }

    // Load base service structure from API
    const loadServiceStructure = async () => {
      try {
        isLoading.value = true
        
        const response = await fetch('/api/public/centers/filters?api_key=map-key-abc123')
        const data = await response.json()
        
        if (data.success && data.data.three_level_structure) {
          serviceStructure.value = data.data.three_level_structure
          console.log('✅ Three-level service structure loaded:', serviceStructure.value)
        } else {
          console.error('❌ Failed to load service structure:', data.message)
        }
      } catch (error) {
        console.error('❌ Error loading service structure:', error)
      } finally {
        isLoading.value = false
      }
    }

    // Dynamically check third level data when selecting second level service
    const checkForThirdLevel = async (category, serviceType) => {
      try {
        // Check if third level data is already cached
        const categoryData = serviceStructure.value[category]
        const serviceTypeData = categoryData?.services?.[serviceType]
        
        if (serviceTypeData && serviceTypeData.subtypes !== undefined) {
          // Data already exists, no need to call API again
          return
        }
        
        // Call API to check if third level data exists
        const response = await fetch(`/api/public/centers/check-third-level?category=${category}&serviceType=${serviceType}&api_key=map-key-abc123`)
        const data = await response.json()
        
        if (data.success && data.data.hasThirdLevel) {
          // Update service structure with third level data
          if (!serviceStructure.value[category]) {
            serviceStructure.value[category] = { services: {} }
          }
          if (!serviceStructure.value[category].services[serviceType]) {
            serviceStructure.value[category].services[serviceType] = {}
          }
          
          serviceStructure.value[category].services[serviceType].subtypes = data.data.subtypes
          console.log(`✅ Third level data loaded for ${category} → ${serviceType}:`, data.data.subtypes)
        } else {
          // No third level, set to null
          if (serviceStructure.value[category]?.services?.[serviceType]) {
            serviceStructure.value[category].services[serviceType].subtypes = null
          }
          console.log(`ℹ️ No third level available for ${category} → ${serviceType}`)
        }
      } catch (error) {
        console.error('❌ Error checking third level:', error)
      }
    }

    // Event handlers
    const onCategoryChange = () => {
      selectedServiceType.value = 'all'
      selectedSpecificService.value = 'all'
      emitFilters()
    }

    const onServiceTypeChange = async () => {
      selectedSpecificService.value = 'all'
      
      // Dynamically check if third level data exists
      if (selectedCategory.value !== 'all' && selectedServiceType.value !== 'all') {
        await checkForThirdLevel(selectedCategory.value, selectedServiceType.value)
      }
      
      emitFilters()
    }

    const onSpecificServiceChange = () => {
      emitFilters()
    }

    const resetFilters = () => {
      selectedCategory.value = 'all'
      selectedServiceType.value = 'all'
      selectedSpecificService.value = 'all'
      emitFilters()
    }

    const clearCategory = () => {
      selectedCategory.value = 'all'
      selectedServiceType.value = 'all'
      selectedSpecificService.value = 'all'
      emitFilters()
    }

    const clearServiceType = () => {
      selectedServiceType.value = 'all'
      selectedSpecificService.value = 'all'
      emitFilters()
    }

    const clearSpecificService = () => {
      selectedSpecificService.value = 'all'
      emitFilters()
    }

    const emitFilters = () => {
      emit('filtersChanged', {
        category: selectedCategory.value,
        serviceType: selectedServiceType.value,
        specificService: selectedSpecificService.value,
        serviceQuery: currentServiceQuery.value
      })
    }

    // Initialize component
    onMounted(() => {
      loadServiceStructure()
    })

    // Watch for prop changes
    watch(() => props.initialFilters, (newFilters) => {
      selectedCategory.value = newFilters.category || 'all'
      selectedServiceType.value = newFilters.serviceType || 'all'
      selectedSpecificService.value = newFilters.specificService || 'all'
    }, { deep: true })

    return {
      // Loading state
      isLoading,
      
      // Data
      serviceStructure,
      availableServiceTypes,
      availableSpecificServices,
      
      // Filter state
      selectedCategory,
      selectedServiceType,
      selectedSpecificService,
      
      // Computed
      hasActiveFilters,
      currentServiceQuery,
      
      // Methods
      getCategoryLabel,
      getServiceTypeLabel,
      getSpecificServiceLabel,
      onCategoryChange,
      onServiceTypeChange,
      onSpecificServiceChange,
      resetFilters,
      clearCategory,
      clearServiceType,
      clearSpecificService
    }
  }
}
</script>

<style scoped>
.service-cascade-filter select {
  transition: all 0.2s ease-in-out;
}

.service-cascade-filter select:focus {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}
</style> 